import { useCallback, ReactNode, useMemo } from 'react';
import {
    DataGrid as MuiDataGrid,
    GridColDef,
    GridRowParams,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridRowIdGetter,
    GridValidRowModel,
    GridSortModel,
    GridColumnVisibilityModel,
    GridToolbarProps,
} from '@mui/x-data-grid';
import { Box, CircularProgress, Typography, alpha, useTheme } from '@mui/material';

interface CustomToolbarProps extends GridToolbarProps {
    toolbar?: ReactNode;
}

declare module '@mui/x-data-grid' {
    interface NoRowsOverlayPropsOverrides {
        message?: ReactNode;
    }
}

interface DataGridProps<T extends GridValidRowModel> {
    columns: GridColDef[];
    rows: T[];
    loading?: boolean;
    getRowId?: GridRowIdGetter<T>;
    onRowClick?: (row: T) => void;
    /** Custom content to render in the toolbar (left side) */
    toolbar?: ReactNode;
    /** Message to show when there are no rows. Accepts a node so callers can offer a retry. */
    emptyMessage?: ReactNode;
    /** Initial sort model */
    initialSortModel?: GridSortModel;
    /** Initial column visibility */
    columnVisibilityModel?: GridColumnVisibilityModel;
}

function LoadingOverlay() {
    const theme = useTheme();
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                backgroundColor: alpha(theme.palette.background.paper, 0.7),
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 10,
            }}
        >
            <CircularProgress />
        </Box>
    );
}

function NoRowsOverlay({ message }: { message?: ReactNode }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                p: 2,
                textAlign: 'center',
            }}
        >
            {typeof message === 'string' || message == null ? (
                <Typography sx={{ color: 'text.secondary' }}>{message ?? 'No data'}</Typography>
            ) : (
                message
            )}
        </Box>
    );
}

function CustomToolbar(props: CustomToolbarProps) {
    return (
        <GridToolbarContainer sx={{ p: 1, gap: 1 }}>
            {props.toolbar}
            <Box sx={{ flexGrow: 1 }} />
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
        </GridToolbarContainer>
    );
}

const STABLE_SLOTS = {
    toolbar: CustomToolbar,
    loadingOverlay: LoadingOverlay,
    noRowsOverlay: NoRowsOverlay,
};

export default function DataGrid<T extends GridValidRowModel>({
    columns,
    rows,
    loading = false,
    getRowId,
    onRowClick,
    toolbar,
    emptyMessage,
    initialSortModel,
    columnVisibilityModel,
}: DataGridProps<T>) {
    // Dynamic page size options based on data length
    const pageSizeOptions = useMemo(() => {
        const defaultOptions = [100, 500];
        if (rows.length > 0 && rows.length < 100) {
            return [rows.length, ...defaultOptions];
        }
        return defaultOptions;
    }, [rows.length]);

    const handleRowClick = useCallback(
        (params: GridRowParams<T>) => {
            onRowClick?.(params.row);
        },
        [onRowClick],
    );

    const slotProps = useMemo(
        () => ({
            toolbar: { toolbar },
            noRowsOverlay: { message: emptyMessage },
        }),
        [toolbar, emptyMessage],
    );

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0, // Critical for flex children to shrink properly
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <MuiDataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                getRowId={getRowId}
                onRowClick={onRowClick ? handleRowClick : undefined}
                slots={STABLE_SLOTS}
                slotProps={slotProps}
                showToolbar
                pageSizeOptions={pageSizeOptions}
                initialState={{
                    pagination: {
                        paginationModel: { pageSize: pageSizeOptions[0] },
                    },
                    sorting: initialSortModel ? { sortModel: initialSortModel } : undefined,
                }}
                columnVisibilityModel={columnVisibilityModel}
                disableRowSelectionOnClick
                sx={{
                    flex: 1,
                    // Sticky header
                    '& .MuiDataGrid-columnHeaders': {
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'background.paper',
                        zIndex: 1,
                    },
                    /**
                     * Vertically centre every cell. Cells render a mix of raw strings, Typography
                     * and flex components, and without this each lands on its own baseline — the
                     * device name high, the status icon mid, the timestamp low, all in one row.
                     */
                    '& .MuiDataGrid-cell': {
                        display: 'flex',
                        alignItems: 'center',
                    },
                    // Row hover cursor when clickable
                    ...(onRowClick && {
                        '& .MuiDataGrid-row:hover': {
                            cursor: 'pointer',
                        },
                    }),
                }}
            />
        </Box>
    );
}
